/**
 * Catálogos globais — Aptidões e Exames.
 *
 * São os dois cadastros que não pertencem a empresa nem a documento, e eram os
 * dois sem tela. O de exames mostra o custo disso: nasceu da extração de cada
 * PCMSO e hoje tem "Clínico", "Exame Clínico" e "Avaliação Clínica" como
 * registros distintos. Ninguém viu degradar porque não havia onde olhar.
 *
 * Por isso a coluna de USO é o centro da tela, não um detalhe: é o número que
 * diz se dá pra renomear ou mesclar sem quebrar o que está em operação.
 */
import { useState } from "react";

import {
  useAptidoes,
  useCatalogoExames,
  useCriarAptidao,
  useMesclarAptidao,
  useMesclarExame,
  useRenomearAptidao,
  useRenomearExame,
} from "@/hooks/useApi";
import type { AptidaoCatalogo, ExameCatalogo } from "@/hooks/useApi";

type Aba = "aptidoes" | "exames";

export function CatalogosPage() {
  const [aba, setAba] = useState<Aba>("aptidoes");

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-2xl font-semibold">Catálogos</h2>
        <p className="text-sm text-ink-muted">
          Cadastros compartilhados por todas as empresas. O que está aqui vai
          impresso no ASO.
        </p>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {([["aptidoes", "Aptidões"], ["exames", "Exames"]] as const).map(([chave, rotulo]) => (
          <button
            key={chave}
            className={`px-4 py-2 text-sm font-medium ${
              aba === chave
                ? "border-b-2 border-anarosa text-anarosa"
                : "text-ink-muted hover:text-ink"
            }`}
            onClick={() => setAba(chave)}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {aba === "aptidoes" ? <AbaAptidoes /> : <AbaExames />}
    </div>
  );
}

// ---------------------------------------------------------------------
// Aptidões
// ---------------------------------------------------------------------
function AbaAptidoes() {
  const { data, isLoading } = useAptidoes();
  const criar = useCriarAptidao();
  const renomear = useRenomearAptidao();
  const mesclar = useMesclarAptidao();

  const [novoNome, setNovoNome] = useState("");
  const [editando, setEditando] = useState<AptidaoCatalogo | null>(null);
  const [form, setForm] = useState({ nome: "", frase_aso: "" });
  const [mesclarDe, setMesclarDe] = useState<AptidaoCatalogo | null>(null);
  const [destino, setDestino] = useState<number | null>(null);

  const erro = (criar.error ?? renomear.error ?? mesclar.error) as Error | undefined;

  return (
    <div className="space-y-3">
      <div className="card p-3">
        <label className="mb-1 block text-xs font-medium text-ink-muted">Nova aptidão</label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input w-80"
            placeholder="ex.: Trabalho com Eletricidade"
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
          />
          <button
            className="btn-primary"
            disabled={novoNome.trim().length < 3 || criar.isPending}
            onClick={async () => {
              try {
                await criar.mutateAsync({ nome: novoNome.trim() });
                setNovoNome("");
              } catch { /* erro abaixo */ }
            }}
          >
            {criar.isPending ? "Criando…" : "Criar"}
          </button>
          <span className="text-xs text-ink-muted">
            A frase do ASO sai como “Apto para {novoNome.trim() || "…"}”.
          </span>
        </div>
      </div>

      {isLoading ? (
        <p className="text-ink-muted">Carregando…</p>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Aptidão</th>
                <th className="px-4 py-3">Frase impressa no ASO</th>
                <th className="px-4 py-3">Usada em</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data ?? []).map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <span className="font-medium">{a.nome}</span>
                    <span className="ml-2 font-mono text-xs text-ink-subtle">{a.chave}</span>
                  </td>
                  <td className="px-4 py-2 text-ink-muted">{a.frase_aso}</td>
                  <td className="px-4 py-2 text-ink-muted">
                    {a.usos === 0 ? (
                      <span className="text-ink-subtle">nenhum GSE</span>
                    ) : (
                      `${a.usos} GSE${a.usos > 1 ? "s" : ""}`
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-sm font-medium text-anarosa hover:underline"
                      onClick={() => {
                        setForm({ nome: a.nome, frase_aso: a.frase_aso });
                        setEditando(a);
                      }}
                    >
                      Renomear
                    </button>
                    <button
                      className="ml-3 text-sm font-medium text-ink-muted hover:underline"
                      onClick={() => { setDestino(null); setMesclarDe(a); }}
                    >
                      Mesclar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {erro ? <p className="text-sm text-anarosa">{erro.message}</p> : null}

      {editando ? (
        <Modal titulo={`Renomear “${editando.nome}”`} onClose={() => setEditando(null)}>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Nome</label>
            <input className="input mt-1" value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">
              Frase impressa no ASO
            </label>
            <input className="input mt-1" value={form.frase_aso}
              onChange={(e) => setForm({ ...form, frase_aso: e.target.value })} />
          </div>
          <Aviso>
            A chave <strong>{editando.chave}</strong> não muda — é por ela que a leitura
            dos PCMSOs reconhece a aptidão. Renomear afeta os ASOs emitidos
            <strong> daqui em diante</strong>; os já emitidos não mudam.
          </Aviso>
          <Rodape
            carregando={renomear.isPending}
            desabilitado={form.nome.trim().length < 3}
            onCancelar={() => setEditando(null)}
            onConfirmar={async () => {
              try {
                await renomear.mutateAsync({
                  id: editando.id, nome: form.nome.trim(), frase_aso: form.frase_aso.trim(),
                });
                setEditando(null);
              } catch { /* erro na lista */ }
            }}
          />
        </Modal>
      ) : null}

      {mesclarDe ? (
        <Modal titulo={`Mesclar “${mesclarDe.nome}”`} onClose={() => setMesclarDe(null)}>
          <p className="text-sm">
            Os {mesclarDe.usos} vínculo(s) de <strong>{mesclarDe.nome}</strong> passam para a
            aptidão escolhida, e ela é removida do catálogo.
          </p>
          <select className="input" value={destino ?? ""}
            onChange={(e) => setDestino(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Manter qual aptidão?</option>
            {(data ?? []).filter((x) => x.id !== mesclarDe.id).map((x) => (
              <option key={x.id} value={x.id}>{x.nome}</option>
            ))}
          </select>
          <Aviso>
            Vale também para o que já foi marcado em pareceres. Use só quando forem
            o <strong>mesmo</strong> item escrito de formas diferentes — não para juntar
            aptidões parecidas.
          </Aviso>
          <Rodape
            carregando={mesclar.isPending}
            desabilitado={destino === null}
            rotulo="Mesclar"
            onCancelar={() => setMesclarDe(null)}
            onConfirmar={async () => {
              try {
                await mesclar.mutateAsync({ id: mesclarDe.id, destino_id: destino! });
                setMesclarDe(null);
              } catch { /* erro na lista */ }
            }}
          />
        </Modal>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Exames
// ---------------------------------------------------------------------
function AbaExames() {
  const [q, setQ] = useState("");
  const { data, isLoading } = useCatalogoExames(q);
  const renomear = useRenomearExame();
  const mesclar = useMesclarExame();

  const [editando, setEditando] = useState<ExameCatalogo | null>(null);
  const [nome, setNome] = useState("");
  const [mesclarDe, setMesclarDe] = useState<ExameCatalogo | null>(null);
  const [destino, setDestino] = useState<number | null>(null);

  const erro = (renomear.error ?? mesclar.error) as Error | undefined;

  return (
    <div className="space-y-3">
      <input
        className="input max-w-md"
        placeholder="Buscar exame…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      {isLoading ? (
        <p className="text-ink-muted">Carregando…</p>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Exame</th>
                <th className="px-4 py-3">Exigido por</th>
                <th className="px-4 py-3">Já realizado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data ?? []).map((e) => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{e.nome}</td>
                  <td className="px-4 py-2 text-ink-muted">
                    {e.usos_ghe === 0 ? (
                      <span className="text-ink-subtle">nenhum GSE</span>
                    ) : `${e.usos_ghe} GSE${e.usos_ghe > 1 ? "s" : ""}`}
                  </td>
                  <td className="px-4 py-2 text-ink-muted">
                    {e.usos_realizados === 0 ? (
                      <span className="text-ink-subtle">—</span>
                    ) : `${e.usos_realizados}×`}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-sm font-medium text-anarosa hover:underline"
                      onClick={() => { setNome(e.nome); setEditando(e); }}
                    >
                      Renomear
                    </button>
                    <button
                      className="ml-3 text-sm font-medium text-ink-muted hover:underline"
                      onClick={() => { setDestino(null); setMesclarDe(e); }}
                    >
                      Mesclar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {erro ? <p className="text-sm text-anarosa">{erro.message}</p> : null}

      {editando ? (
        <Modal titulo={`Renomear “${editando.nome}”`} onClose={() => setEditando(null)}>
          <input className="input" value={nome} autoFocus
            onChange={(e) => setNome(e.target.value)} />
          <Aviso>
            O nome sai em CAIXA ALTA na tabela do ASO, então a correção aqui é de
            grafia e conteúdo — não de maiúsculas.
          </Aviso>
          <Rodape
            carregando={renomear.isPending}
            desabilitado={nome.trim().length < 2}
            onCancelar={() => setEditando(null)}
            onConfirmar={async () => {
              try {
                await renomear.mutateAsync({ id: editando.id, nome: nome.trim() });
                setEditando(null);
              } catch { /* erro na lista */ }
            }}
          />
        </Modal>
      ) : null}

      {mesclarDe ? (
        <Modal titulo={`Mesclar “${mesclarDe.nome}”`} onClose={() => setMesclarDe(null)}>
          <p className="text-sm">
            As {mesclarDe.usos_ghe} exigência(s) de GSE e os {mesclarDe.usos_realizados} exame(s)
            já realizados passam para o exame escolhido, e <strong>{mesclarDe.nome}</strong> é
            removido do catálogo.
          </p>
          <select className="input" value={destino ?? ""}
            onChange={(e) => setDestino(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Manter qual exame?</option>
            {(data ?? []).filter((x) => x.id !== mesclarDe.id).map((x) => (
              <option key={x.id} value={x.id}>{x.nome}</option>
            ))}
          </select>
          <Aviso>
            Exame realizado entra no cálculo de pendências do colaborador:
            mesclar <strong>muda o que a tela de Pendências mostra</strong>. Use só
            quando forem o mesmo exame escrito de formas diferentes.
          </Aviso>
          <Rodape
            carregando={mesclar.isPending}
            desabilitado={destino === null}
            rotulo="Mesclar"
            onCancelar={() => setMesclarDe(null)}
            onConfirmar={async () => {
              try {
                await mesclar.mutateAsync({ id: mesclarDe.id, destino_id: destino! });
                setMesclarDe(null);
              } catch { /* erro na lista */ }
            }}
          />
        </Modal>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Pedaços compartilhados pelas duas abas
// ---------------------------------------------------------------------
function Modal(
  { titulo, children, onClose }:
  { titulo: string; children: React.ReactNode; onClose: () => void },
) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}>
      <div className="card w-full max-w-md space-y-3 p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold">{titulo}</h3>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Aviso({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      {children}
    </div>
  );
}

function Rodape(
  { carregando, desabilitado, rotulo = "Salvar", onCancelar, onConfirmar }:
  {
    carregando: boolean; desabilitado: boolean; rotulo?: string;
    onCancelar: () => void; onConfirmar: () => void;
  },
) {
  return (
    <div className="flex items-center justify-end gap-2">
      <button className="btn-secondary" onClick={onCancelar}>Cancelar</button>
      <button className="btn-primary" disabled={desabilitado || carregando} onClick={onConfirmar}>
        {carregando ? "Salvando…" : rotulo}
      </button>
    </div>
  );
}
